import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  BADGE_SX,
  CLAMP_TWO_LINES_SX,
} from './crmDashboardStyles';

function MetadataChip({ children, muted = false, tone = 'neutral' }) {
  const toneSx =
    tone === 'type'
      ? {
          color: 'primary.main',
          backgroundColor: 'rgba(0,121,107,0.08)',
          borderColor: 'rgba(0,121,107,0.16)',
        }
      : tone === 'unassigned'
        ? {
            color: '#ad5a00',
            backgroundColor: 'rgba(237,108,2,0.14)',
            borderColor: 'rgba(237,108,2,0.28)',
          }
      : muted
        ? {
            color: 'text.secondary',
            backgroundColor: 'rgba(0,0,0,0.04)',
            borderColor: 'rgba(0,0,0,0.08)',
          }
        : {
            color: '#0d47a1',
            backgroundColor: 'rgba(13,71,161,0.08)',
            borderColor: 'rgba(13,71,161,0.16)',
          };

  return (
    <Box component="span" sx={{ ...BADGE_SX, ...toneSx }}>
      {children}
    </Box>
  );
}

const CrmDashboardTaskCard = memo(function CrmDashboardTaskCard({ item }) {
  const toneSx =
    item.tone === 'overdue'
      ? {
          borderColor: 'rgba(198,40,40,0.24)',
          backgroundColor: 'rgba(255,248,248,0.96)',
          dueColor: '#b71c1c',
        }
      : item.tone === 'today'
        ? {
            borderColor: 'rgba(255,193,7,0.34)',
            backgroundColor: 'rgba(255,252,244,0.96)',
            dueColor: '#7a4b00',
          }
        : {
            borderColor: 'rgba(0,0,0,0.06)',
            backgroundColor: '#fcfdfd',
            dueColor: 'primary.main',
          };
  const unassignedSx = item.isUnassigned
    ? {
        borderColor: 'rgba(237,108,2,0.30)',
        backgroundImage:
          'linear-gradient(90deg, rgba(237,108,2,0.10), rgba(237,108,2,0) 48px)',
        boxShadow: 'inset 4px 0 0 rgba(237,108,2,0.68)',
      }
    : null;

  return (
    <Paper
      component={Link}
      to={item.href}
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.06)',
        textDecoration: 'none',
        color: 'text.primary',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: item.isUnassigned
            ? 'inset 4px 0 0 rgba(237,108,2,0.68), 0 12px 26px rgba(0,0,0,0.08)'
            : '0 12px 26px rgba(0,0,0,0.08)',
        },
        ...toneSx,
        ...unassignedSx,
      }}
    >
      <Stack spacing={0.8}>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
          <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 0 }}>
            {item.title}
          </Typography>
          <ArrowForwardRoundedIcon
            fontSize="small"
            sx={{ color: 'text.secondary', flexShrink: 0, mt: 0.15 }}
          />
        </Stack>

        <Stack spacing={0.2}>
          <Typography variant="body2" fontWeight={700} sx={{ color: toneSx.dueColor }}>
            {item.primaryDueLabel}
          </Typography>
          {item.secondaryDueLabel && (
            <Typography variant="caption" color="text.secondary">
              {item.secondaryDueLabel}
            </Typography>
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {item.customerLabel}
        </Typography>

        {item.notePreview && (
          <Typography variant="body2" color="text.secondary" sx={CLAMP_TWO_LINES_SX}>
            {item.notePreview}
          </Typography>
        )}

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <MetadataChip tone="type">{item.activityTypeLabel}</MetadataChip>
          <MetadataChip tone={item.isUnassigned ? 'unassigned' : 'neutral'}>
            {`Assignee: ${item.assigneeLabel}`}
          </MetadataChip>
          {item.contactLabel && (
            <MetadataChip muted={item.contactMuted}>{item.contactLabel}</MetadataChip>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
});

export default CrmDashboardTaskCard;
